import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ArrowRight } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";

const STATUS_STYLE = {
    created: "border-zinc-200 bg-zinc-50 text-zinc-700",
    payment_pending: "border-amber-200 bg-amber-50 text-amber-700",
    paid: "border-emerald-200 bg-emerald-50 text-emerald-700",
    payment_failed: "border-red-200 bg-red-50 text-red-700",
    cancelled: "border-zinc-200 bg-zinc-50 text-zinc-500",
};
const STATUS_LABEL = {
    created: "Created",
    payment_pending: "Payment pending",
    paid: "Paid",
    payment_failed: "Failed",
    cancelled: "Cancelled",
};

const OrdersList = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const { data } = await api.get("/orders");
                if (active) setItems(data);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, []);

    if (loading) {
        return (
            <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-20 skeleton rounded-2xl" />)}
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <EmptyState
                title="No orders yet"
                description="When you complete a purchase, you'll see your order history here."
                action={<Link to="/products" className="btn-brand">Start shopping</Link>}
            />
        );
    }

    return (
        <ul className="space-y-3" data-testid="orders-list">
            {items.map((o) => (
                <li key={o.id}>
                    <Link
                        to={`/orders/${o.id}`}
                        className="flex items-center gap-4 rounded-2xl border border-zinc-100 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-brand"
                        data-testid={`order-row-${o.id}`}
                    >
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
                            <Package className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-zinc-900">{o.order_no}</p>
                            <p className="text-xs text-zinc-500">{new Date(o.created_at).toLocaleString()} • {o.total_items} item{o.total_items === 1 ? "" : "s"}</p>
                        </div>
                        <span className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[o.status] || STATUS_STYLE.created}`} data-testid={`order-status-${o.id}`}>
                            {STATUS_LABEL[o.status] || o.status}
                        </span>
                        <p className="ml-2 hidden text-sm font-semibold text-zinc-900 sm:block">₹{o.total.toFixed(2)}</p>
                        <ArrowRight className="h-4 w-4 text-zinc-400" />
                    </Link>
                </li>
            ))}
        </ul>
    );
};

const Orders = () => (
    <Layout>
        <section className="mx-auto max-w-5xl px-6 py-10 lg:px-12">
            <header className="mb-8">
                <p className="label-uppercase">History</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">Your orders</h1>
            </header>
            <AuthGuard>
                <OrdersList />
            </AuthGuard>
        </section>
    </Layout>
);

export default Orders;
